import React, { useState, useRef } from "react";
import { App } from "antd";
import axios from "axios";
import "./QRLink.css";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import QRTypeTabs from "../../components/QRTypeTabs";
import { QRCodeCanvas } from "qrcode.react";
import {
  LinkOutlined,
  QrcodeOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { downloadCanvasPng, downloadQrAttachment, downloadRemoteFile, safeFileName } from "../../utils/downloadFile";

const QR_COLORS = ["#000000", "#1a1a2e", "#e5322d", "#0891b2", "#1a7a43", "#6366f1"];
const QR_SIZES  = [
  { label: "Small",  value: 180 },
  { label: "Medium", value: 250 },
  { label: "Large",  value: 320 },
];

const QRLink = () => {
  const { message } = App.useApp();
  const [name,      setName]      = useState("");
  const [link,      setLink]      = useState("");
  const [qrResult,  setQrResult]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [qrColor,   setQrColor]   = useState("#000000");
  const [qrSize,    setQrSize]    = useState(250);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const canvasWrapRef = useRef(null);

  const isModule = window.location.pathname.includes("/dashboard/");

  // ── API — unchanged logic ──────────────────────────────────────────
  const generateQR = async () => {
    if (!link.trim()) { message.warning("Please enter a website URL."); return; }
    if (!name.trim()) { message.warning("Please give your QR a name."); return; }
    if (password && password.length < 6) {
      message.warning("Password must be at least 6 characters.");
      return;
    }
    if (password && password !== confirmPassword) {
      message.warning("Password and confirm password must match.");
      return;
    }

    setLoading(true);
    const authToken = localStorage.getItem("token") || "";

    try {
      const formData = new FormData();
      formData.append("qrType", "Website");
      formData.append("title", name);
      formData.append("source", isModule ? "Web" : "Public-Web");
      formData.append("url", link);
      formData.append("size", QR_SIZES.find((size) => size.value === qrSize)?.label || "Medium");
      formData.append("color", qrColor);
      formData.append("backgroundColor", "#FFFFFF");
      formData.append("payloadData", JSON.stringify({ qrType: "Website", title: name, source: isModule ? "Web" : "Public-Web", url: link }));
      if (password) {
        formData.append("password", password);
        formData.append("confirmPassword", confirmPassword);
      }

      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined;
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Qr/create`,
        formData,
        headers ? { headers } : undefined
      );

      if (response.data.error === false) {
        const data = response.data.data || {};
        setQrResult({
          id: data.id,
          qrValue: data.qrUrl || link,
          qrImageUrl: data.qrImageUrl || "",
          qrSvgUrl: data.qrSvgUrl || "",
          displayValue: link,
          title: data.title || name,
        });
        setName("");
        setLink("");
        setPassword("");
        setConfirmPassword("");
        message.success("QR Code generated!");
      } else {
        message.error(response.data.message || "Failed to generate QR code.");
      }
    } catch {
      message.error("Failed to generate QR code.");
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = async () => {
    if (qrResult?.id && localStorage.getItem("token")) {
      try {
        await downloadQrAttachment({
          baseUrl: process.env.REACT_APP_BASE_URL,
          id: qrResult.id,
          token: localStorage.getItem("token") || "",
          title: qrResult.title || name || "qr-code",
          format: "png",
        });
        return;
      } catch {
        // Fall through to image/canvas download.
      }
    }

    if (qrResult?.qrImageUrl) {
      try {
        await downloadRemoteFile(qrResult.qrImageUrl, safeFileName(qrResult.title || name || "qr-code", "png"));
      } catch {
        const canvas = canvasWrapRef.current?.querySelector("canvas");
        if (canvas) {
          downloadCanvasPng(canvas, safeFileName(qrResult.title || name || "qr-code", "png"));
        } else {
          message.error("Unable to download QR image. Please try again.");
        }
      }
      return;
    }

    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const pngUrl   = canvas.toDataURL("image/png");
    const anchor   = document.createElement("a");
    anchor.download = `${(qrResult?.title || name).replace(/\s+/g, "-").toLowerCase() || "qr-code"}.png`;
    anchor.href    = pngUrl;
    anchor.click();
  };

  const reset = () => {
    setName("");
    setLink("");
    setPassword("");
    setConfirmPassword("");
    setQrResult(null);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") generateQR(); };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`qrl-root ${isModule ? "qrl-module" : ""}`}>
        <div className="qrl-inner">
          <QRTypeTabs activeType="Website QR" />

          {/* ── Left: form ── */}
          <div className="qrl-form-side">

            {/* Page heading */}
            <div className="qrl-heading">
              <div className="qrl-heading-icon">
                <LinkOutlined />
              </div>
              <div>
                <h1 className="qrl-title">Link to QR Code</h1>
                <p className="qrl-subtitle">
                  Turn any URL into a scannable QR code instantly.
                </p>
              </div>
            </div>

            <div className="qrl-card">

              <div className="qrl-field">
                <label className="qrl-label">QR Code Name</label>
                <input
                  className="qrl-input"
                  placeholder="e.g. My Portfolio"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              <div className="qrl-field">
                <label className="qrl-label">Website URL</label>
                <div className="qrl-input-wrap">
                  <LinkOutlined className="qrl-input-icon" />
                  <input
                    className="qrl-input qrl-input-icon-pad"
                    placeholder="https://www.yourwebsite.com"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>

              {/* Customisation */}
              <div className="qrl-divider" />

              <div className="qrl-field">
                <label className="qrl-label">QR Color</label>
                <div className="qrl-color-row">
                  {QR_COLORS.map((c) => (
                    <button
                      key={c}
                      className={`qrl-color-swatch ${qrColor === c ? "active" : ""}`}
                      style={{ background: c }}
                      onClick={() => setQrColor(c)}
                      title={c}
                    />
                  ))}
                  <input
                    type="color"
                    className="qrl-color-custom"
                    value={qrColor}
                    onChange={(e) => setQrColor(e.target.value)}
                    title="Custom color"
                  />
                </div>
              </div>

              <div className="qrl-field">
                <label className="qrl-label">QR Size</label>
                <div className="qrl-size-row">
                  {QR_SIZES.map((s) => (
                    <button
                      key={s.value}
                      className={`qrl-size-btn ${qrSize === s.value ? "active" : ""}`}
                      onClick={() => setQrSize(s.value)}
                    >
                      {s.label}
                      <span>{s.value}px</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="qrl-field">
                <label className="qrl-label">Password Protection</label>
                <input
                  className="qrl-input"
                  type="password"
                  placeholder="Optional password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              {password && (
                <div className="qrl-field">
                  <label className="qrl-label">Confirm Password</label>
                  <input
                    className="qrl-input"
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                </div>
              )}

              {/* Actions */}
              <div className="qrl-actions">
                <button
                  className="qrl-generate-btn"
                  onClick={generateQR}
                  disabled={loading}
                >
                  {loading
                    ? <span className="qrl-spinner" />
                    : <QrcodeOutlined />
                  }
                  {loading ? "Generating…" : "Generate QR Code"}
                </button>

                {qrResult && (
                  <button className="qrl-reset-btn" onClick={reset}>
                    <ReloadOutlined /> Start over
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Right: preview ── */}
          <div className="qrl-preview-side">
            <div className="qrl-preview-card">

              <div className="qrl-preview-label">QR Preview</div>

              {qrResult ? (
                <>
                  <div className="qrl-qr-wrap" ref={canvasWrapRef}>
                    {qrResult.qrImageUrl ? (
                      <>
                        <img src={qrResult.qrImageUrl} alt={qrResult.title} className="qrl-qr-image" />
                        <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
                          <QRCodeCanvas
                            value={qrResult.qrValue}
                            size={qrSize}
                            fgColor={qrColor}
                            bgColor="#ffffff"
                            level="H"
                            includeMargin={true}
                          />
                        </div>
                      </>
                    ) : (
                      <QRCodeCanvas
                        value={qrResult.qrValue}
                        size={qrSize}
                        fgColor={qrColor}
                        bgColor="#ffffff"
                        level="H"
                        includeMargin={true}
                      />
                    )}
                  </div>

                  <div className="qrl-preview-success">
                    <CheckCircleFilled className="qrl-success-icon" />
                    <span>QR code ready!</span>
                  </div>

                  <div className="qrl-preview-name">{qrResult.title}</div>

                  <div className="qrl-preview-url">
                    <LinkOutlined />
                    <span>{qrResult.displayValue.length > 40 ? qrResult.displayValue.slice(0, 40) + "…" : qrResult.displayValue}</span>
                  </div>

                  <button className="qrl-download-btn" onClick={downloadQR}>
                    <DownloadOutlined /> Download PNG
                  </button>
                </>
              ) : (
                <div className="qrl-placeholder">
                  <div className="qrl-placeholder-icon">
                    <QrcodeOutlined />
                  </div>
                  <div className="qrl-placeholder-text">
                    Fill in the form and click<br />
                    <strong>Generate QR Code</strong>
                  </div>
                </div>
              )}
            </div>

            {/* Tips card */}
            <div className="qrl-tips-card">
              <div className="qrl-tips-title">💡 Tips</div>
              <ul className="qrl-tips-list">
                <li>Always include <code>https://</code> in your URL</li>
                <li>Use <strong>Large</strong> size for print materials</li>
                <li>Dark colors work best for scanning</li>
                <li>Test your QR before printing</li>
              </ul>
            </div>
          </div>

        </div>
      </div>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default QRLink;
